// Obsługa czytnika kodów kreskowych / QR (2026-09-23).
//
// Czytniki USB i Bluetooth — niezależnie od tego, którym są podłączone — zgłaszają się
// systemowi jako KLAWIATURA. Nie ma żadnego API, przez które dałoby się je „otworzyć":
// urządzenie po prostu „wpisuje" zeskanowany kod tam, gdzie akurat stoi kursor, i na
// końcu dokłada znak zatwierdzenia (fabrycznie Enter, w części modeli Tab).
//
// Stąd całe zadanie sprowadza się do JEDNEGO pytania: czy ten ciąg znaków wystukał
// człowiek, czy urządzenie. Rozstrzyga to tempo. Czytnik wysyła znaki co 5–20 ms;
// bardzo szybki maszynista (120 słów na minutę) potrzebuje około 100 ms na znak.
// Próg 50 ms leży więc wygodnie pomiędzy — z zapasem na wolniejsze połączenia Bluetooth
// i bez ryzyka, że ręczne pisanie zostanie wzięte za skan.
//
// Moduł jest CZYSTY (bez React, bez DOM) — decyzję podejmuje wyłącznie na podstawie
// klawiszy i znaczników czasu, więc da się ją przetestować bez przeglądarki i bez czytnika.

export const DOMYSLNA_PRZERWA_MS = 50;
export const DOMYSLNA_DLUGOSC = 4;

// Klawisze, które czytnik wysyła jako zakończenie kodu. Enter jest ustawieniem fabrycznym
// większości modeli, Tab spotyka się w tych konfigurowanych pod wypełnianie formularzy.
const KLAWISZE_KONCZACE = new Set(['Enter', 'NumpadEnter', 'Tab']);

// Klawisze, które MOGĄ pojawić się w środku skanu i nie znaczą nic złego: czytnik wciska
// Shift przy każdej wielkiej literze. Nie zerują bufora i nie liczą się do tempa.
const KLAWISZE_OBOJETNE = new Set(['Shift', 'CapsLock', 'AltGraph', 'Dead', 'Unidentified']);

/**
 * Bufor rozpoznający skan w strumieniu zdarzeń klawiatury.
 *
 * `push(zdarzenie, teraz)` zwraca zeskanowany kod dokładnie w tej chwili, w której skan
 * się zakończył — w każdym innym wypadku `null`.
 *
 * @param {object}  opcje
 * @param {number}  opcje.maxPrzerwaMs  maksymalna przerwa MIĘDZY znakami jednego skanu
 * @param {number}  opcje.minDlugosc    krótsze ciągi ignorujemy (chroni przed przypadkiem)
 */
export function createScanBuffer({ maxPrzerwaMs = DOMYSLNA_PRZERWA_MS, minDlugosc = DOMYSLNA_DLUGOSC } = {}) {
  let bufor = '';
  let ostatniZnak = 0;

  const wyczysc = () => { bufor = ''; ostatniZnak = 0; };

  return {
    push(zdarzenie, teraz) {
      const e = zdarzenie || {};
      const klawisz = e.key;

      // Skrót klawiaturowy to na pewno człowiek — czytnik nie trzyma modyfikatorów.
      if (e.ctrlKey || e.altKey || e.metaKey) { wyczysc(); return null; }

      if (KLAWISZE_OBOJETNE.has(klawisz)) return null;

      if (KLAWISZE_KONCZACE.has(klawisz)) {
        const kod = bufor;
        const odOstatniego = teraz - ostatniZnak;
        wyczysc();
        // Znak kończący musi przyjść W TEJ SAMEJ serii. Bez tego warunku wystarczyło
        // szybko wpisać kilka znaków, pójść po kawę i wcisnąć Enter, żeby panel uznał
        // to za skan. Podwójny próg, bo część czytników robi tu minimalną pauzę.
        if (kod.length >= minDlugosc && odOstatniego <= maxPrzerwaMs * 2) return kod;
        return null;
      }

      // Znak drukowalny: `key` ma wtedy dokładnie jeden znak („a", „7", „-").
      if (typeof klawisz === 'string' && klawisz.length === 1) {
        if (ostatniZnak && (teraz - ostatniZnak) > maxPrzerwaMs) bufor = '';
        bufor += klawisz;
        ostatniZnak = teraz;
        return null;
      }

      // Cokolwiek innego (strzałki, Backspace, F5) — człowiek edytuje, nie skanuje.
      wyczysc();
      return null;
    },

    reset: wyczysc,
    get dlugosc() { return bufor.length; },
  };
}

/**
 * Czy zdarzenie wyszło z pola, w którym użytkownik właśnie pisze.
 *
 * Nasłuch globalny CELOWO odpuszcza takie zdarzenia: skoro kursor stoi w polu, to czytnik
 * wpisuje kod dokładnie tam, gdzie człowiek go postawił, i przekierowywanie go gdzie indziej
 * byłoby odbieraniem decyzji. Przechwytywanie znaków JUŻ wstawionych do pola wymagałoby
 * ich wycofywania — a to widać na ekranie jako miganie tekstu.
 */
export function isEditableTarget(element) {
  if (!element || typeof element !== 'object') return false;
  const tag = String(element.tagName || '').toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag === 'input') {
    const typ = String(element.type || 'text').toLowerCase();
    // Pola, które nie przyjmują tekstu, nie są „pisaniem" — skan ma nad nimi pierwszeństwo.
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color'].includes(typ);
  }
  return element.isContentEditable === true;
}

// Kody bywają zakończone białymi znakami (część czytników dokłada spację przed Enterem).
export function normalizeScannedCode(kod) {
  return String(kod == null ? '' : kod).trim();
}
